import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const EMPTY_FORM = {
  name: '',
  description: '',
  min_age: '',
  max_age: '',
  cutoff_month: '4',
  cutoff_day: '30',
  season_rollover_month: '8',
  season_rollover_day: '1',
  sort_order: '0'
};

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AgeGroupManagement = ({ organizations }) => {
  const [organizationId, setOrganizationId] = useState('');
  const [groups, setGroups] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [mode, setMode] = useState('default');
  const [modeByOrganization, setModeByOrganization] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedOrganization = useMemo(
    () => organizations.find(org => String(org.id) === String(organizationId)),
    [organizations, organizationId]
  );

  useEffect(() => {
    if (!organizationId && organizations.length) setOrganizationId(String(organizations[0].id));
  }, [organizations, organizationId]);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await axios.get('/api/age-groups/presets', authConfig());
        setPresets(response.data || []);
        if (response.data?.length) setSelectedPreset(response.data[0].key);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Unable to load age-group presets');
      }
    };
    loadPresets();
  }, []);

  const loadGroups = async (orgId = organizationId, preferredMode = null) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/age-groups?organizationId=${orgId}&include_global=false`,
        authConfig()
      );
      const organizationGroups = response.data || [];
      setGroups(organizationGroups);
      const configuredMode = preferredMode
        || modeByOrganization[orgId]
        || organizations.find(org => String(org.id) === String(orgId))?.settings?.ageGroupMode;
      setMode(configuredMode || (organizationGroups.length ? 'organization' : 'default'));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to load age groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    loadGroups(organizationId);
    // loadGroups intentionally changes whenever the selected organization changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const changeMode = async (nextMode) => {
    if (!organizationId || nextMode === mode) return;
    try {
      await axios.put(
        `/api/age-groups/organizations/${organizationId}/mode`,
        { mode: nextMode },
        authConfig()
      );
      setMode(nextMode);
      setModeByOrganization(current => ({ ...current, [organizationId]: nextMode }));
      toast.success(nextMode === 'organization' ? 'Organization age groups enabled' : 'Platform defaults enabled');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to change age-group mode');
    }
  };

  const importPreset = async () => {
    if (!selectedPreset || !organizationId) return;
    if (groups.length && !window.confirm('Importing will reset same-named groups to the preset values. Continue?')) return;
    setSaving(true);
    try {
      await axios.post(
        `/api/age-groups/organizations/${organizationId}/import-preset`,
        { preset_key: selectedPreset },
        authConfig()
      );
      setMode('organization');
      setModeByOrganization(current => ({ ...current, [organizationId]: 'organization' }));
      await loadGroups(organizationId, 'organization');
      toast.success('Age-group preset imported. You can customize it below.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to import preset');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = group => {
    setEditingId(group.id);
    setForm({
      name: group.name || '',
      description: group.description || '',
      min_age: group.min_age ?? '',
      max_age: group.max_age ?? '',
      cutoff_month: group.cutoff_month ?? '4',
      cutoff_day: group.cutoff_day ?? '30',
      season_rollover_month: group.season_rollover_month ?? '8',
      season_rollover_day: group.season_rollover_day ?? '1',
      sort_order: group.sort_order ?? '0'
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const saveGroup = async event => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      organization_id: Number(organizationId),
      min_age: form.min_age === '' ? null : Number(form.min_age),
      max_age: form.max_age === '' ? null : Number(form.max_age),
      cutoff_month: Number(form.cutoff_month),
      cutoff_day: Number(form.cutoff_day),
      season_rollover_month: Number(form.season_rollover_month),
      season_rollover_day: Number(form.season_rollover_day),
      sort_order: Number(form.sort_order || 0)
    };
    try {
      if (editingId) {
        await axios.put(`/api/age-groups/${editingId}`, payload, authConfig());
      } else {
        await axios.post('/api/age-groups', payload, authConfig());
      }
      setMode('organization');
      setModeByOrganization(current => ({ ...current, [organizationId]: 'organization' }));
      resetForm();
      await loadGroups(organizationId, 'organization');
      toast.success(editingId ? 'Age group updated' : 'Custom age group added');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to save age group');
    } finally {
      setSaving(false);
    }
  };

  const archiveGroup = async group => {
    if (!window.confirm(`Archive ${group.name}? Existing records will keep their age-group reference.`)) return;
    try {
      await axios.delete(`/api/age-groups/${group.id}`, authConfig());
      if (editingId === group.id) resetForm();
      await loadGroups();
      toast.success('Age group archived');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to archive age group');
    }
  };

  const chosenPreset = presets.find(preset => preset.key === selectedPreset);

  if (!organizations.length) {
    return <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">No organizations are available.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Age-group rules</h2>
            <p className="mt-1 text-sm text-gray-500">Choose platform defaults, import a baseball organization preset, or build a custom ruleset.</p>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            Organization
            <select
              value={organizationId}
              onChange={event => setOrganizationId(event.target.value)}
              className="mt-1 block min-w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
          </label>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <button
            type="button"
            onClick={() => changeMode('default')}
            className={`rounded-lg border p-4 text-left ${mode === 'default' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <span className="block text-sm font-semibold text-gray-900">Platform defaults</span>
            <span className="mt-1 block text-xs text-gray-500">Use the shared age groups maintained by the platform.</span>
          </button>
          <button
            type="button"
            onClick={() => changeMode('organization')}
            className={`rounded-lg border p-4 text-left ${mode === 'organization' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <span className="block text-sm font-semibold text-gray-900">Organization rules</span>
            <span className="mt-1 block text-xs text-gray-500">Use only the imported or custom groups managed below.</span>
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900">Start from a governing-body preset</h3>
        <p className="mt-1 text-sm text-gray-500">Importing copies the rules into {selectedOrganization?.name}; the copy remains fully editable.</p>
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end">
          <label className="flex-1 text-sm font-medium text-gray-700">
            Preset
            <select
              value={selectedPreset}
              onChange={event => setSelectedPreset(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              {presets.map(preset => <option key={preset.key} value={preset.key}>{preset.name}</option>)}
            </select>
          </label>
          <button type="button" onClick={importPreset} disabled={saving || !selectedPreset} className="btn-primary px-4 py-2 text-sm disabled:opacity-50">
            Import preset
          </button>
        </div>
        {chosenPreset && (
          <div className="mt-3 text-xs text-gray-500">
            <p>{chosenPreset.description}</p>
            <p className="mt-1">Includes {chosenPreset.groups.map(group => group.name).join(', ')}.</p>
            <a className="mt-1 inline-block font-medium text-blue-600 hover:underline" href={chosenPreset.source_url} target="_blank" rel="noreferrer">View official source ↗</a>
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <h3 className="font-semibold text-gray-900">Organization age groups</h3>
          </div>
          {loading ? (
            <p className="p-6 text-sm text-gray-500">Loading age groups…</p>
          ) : groups.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No organization-specific groups yet. Import a preset or add one.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {groups.map(group => (
                <div key={group.id} className="flex items-center gap-4 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{group.name}</span>
                      {group.ruleset_key && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">{presets.find(p => p.key === group.ruleset_key)?.name || 'Preset'}</span>}
                    </div>
                    <p className="mt-0.5 text-sm text-gray-500">
                      Ages {group.min_age ?? '–'}–{group.max_age ?? '–'} · age on {group.cutoff_month || 5}/{group.cutoff_day || 1}
                    </p>
                  </div>
                  <button type="button" onClick={() => startEditing(group)} className="text-gray-400 hover:text-blue-600" aria-label={`Edit ${group.name}`}><PencilIcon className="h-5 w-5" /></button>
                  <button type="button" onClick={() => archiveGroup(group)} className="text-gray-400 hover:text-red-600" aria-label={`Archive ${group.name}`}><TrashIcon className="h-5 w-5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={saveGroup} className="h-fit rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900">{editingId ? 'Edit age group' : 'Add a custom age group'}</h3>
          <div className="mt-4 space-y-3">
            <label className="block text-sm font-medium text-gray-700">Name *
              <input required value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} placeholder="Example: 10U" className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>
            <label className="block text-sm font-medium text-gray-700">Description
              <input value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Minimum age" value={form.min_age} onChange={value => setForm({ ...form, min_age: value })} min="0" max="99" />
              <NumberField label="Maximum age" value={form.max_age} onChange={value => setForm({ ...form, max_age: value })} min="0" max="99" />
              <NumberField label="Cutoff month" value={form.cutoff_month} onChange={value => setForm({ ...form, cutoff_month: value })} min="1" max="12" />
              <NumberField label="Cutoff day" value={form.cutoff_day} onChange={value => setForm({ ...form, cutoff_day: value })} min="1" max="31" />
              <NumberField label="Season rolls in month" value={form.season_rollover_month} onChange={value => setForm({ ...form, season_rollover_month: value })} min="1" max="12" />
              <NumberField label="Season rolls on day" value={form.season_rollover_day} onChange={value => setForm({ ...form, season_rollover_day: value })} min="1" max="31" />
              <NumberField label="Display order" value={form.sort_order} onChange={value => setForm({ ...form, sort_order: value })} min="0" />
            </div>
            <p className="text-xs text-gray-500">The cutoff is the date on which playing age is measured. The rollover selects when fall registrations begin using the next playing year.</p>
          </div>
          <div className="mt-5 flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-sm disabled:opacity-50"><PlusIcon className="h-4 w-4" />{editingId ? 'Save changes' : 'Add group'}</button>
            {editingId && <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>}
          </div>
        </form>
      </div>
    </div>
  );
};

const NumberField = ({ label, value, onChange, min, max }) => (
  <label className="block text-sm font-medium text-gray-700">{label}
    <input type="number" value={value} onChange={event => onChange(event.target.value)} min={min} max={max} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2" />
  </label>
);

export default AgeGroupManagement;
